import type {
    Bi,
    Cell,
    Lakara,
    Pada,
    Purusha,
    ReviewStatus,
    Vacana,
    VerbTable
} from '@/sanskrit/lib/sanskrit/types'
import type { Dhatu } from '@/sanskrit/lib/sanskrit/dhatus'
import type { JoinRule } from '@/sanskrit/lib/sanskrit/verb-engine'

import { getDhatu, padasOf } from '@/sanskrit/lib/sanskrit/dhatus'
import { LAKARA_ORDER, PURUSHAS, VACANAS } from '@/sanskrit/lib/sanskrit/types'
import { LAKARA_LABEL, PURUSHA_LABEL, VACANA_LABEL } from '@/sanskrit/lib/sanskrit/labels'

import {
    ENDINGS,
    ENDING_SOURCE,
    JOIN_RULES,
    attach,
    lutTable,
    thematicTable
} from '@/sanskrit/lib/sanskrit/verb-engine'

export interface Paradigm {
    dhatuId: string
    lakara: Lakara
    pada: Pada
    forms: VerbTable
    status: ReviewStatus
    generated: boolean
    viaDhatuId?: string
    note?: Bi
}

const AUGMENT = 'अ'

const startsWithConsonant = (s: string): boolean => /^[क-ह]/.test(s)

const imperfectStems = (d: Dhatu, pada: Pada): string[] | null => {
    const explicit = d.imperfect?.[pada]
    if (explicit) return explicit

    const present = d.present?.[pada]
    if (!present || !present.every(startsWithConsonant)) return null

    return present.map(stem => AUGMENT + stem)
}

const conditionalStems = (d: Dhatu): string[] | null => {
    if (d.conditional) return d.conditional
    if (!d.future || !d.future.every(startsWithConsonant)) return null
    return d.future.map(stem => AUGMENT + stem)
}

/** Build (or look up) one paradigm. Returns null when the platform does not yet carry it for this root. */
export const getParadigm = (dhatuId: string, lakara: Lakara, pada: Pada): Paradigm | null => {
    const d = getDhatu(dhatuId)
    if (!d) return null

    const explicit = d.explicit?.[lakara]?.[pada]

    if (explicit) {
        return {
            dhatuId,
            lakara,
            pada,
            forms: explicit,
            status: d.explicitStatus?.[lakara] ?? d.status,
            generated: false
        }
    }

    const via = d.via?.[lakara]

    if (via) {
        const target = getParadigm(via.dhatuId, lakara, pada)
        if (!target) return null
        return { ...target, dhatuId, viaDhatuId: via.dhatuId, note: via.note }
    }

    if (lakara === 'lat' || lakara === 'lot' || lakara === 'vidhilin') {
        const stems = d.present?.[pada]
        if (!stems) return null
        return { dhatuId, lakara, pada, forms: thematicTable(stems, lakara, pada), status: d.status, generated: true }
    }

    if (lakara === 'lan') {
        const stems = imperfectStems(d, pada)
        if (!stems) return null
        return { dhatuId, lakara, pada, forms: thematicTable(stems, 'lan', pada), status: d.status, generated: true }
    }

    if (lakara === 'lrt') {
        if (!d.future || !padasOf(d).includes(pada)) return null
        return { dhatuId, lakara, pada, forms: thematicTable(d.future, 'lat', pada), status: d.status, generated: true }
    }

    if (lakara === 'lrn') {
        const stems = conditionalStems(d)
        if (!stems || !padasOf(d).includes(pada)) return null
        return { dhatuId, lakara, pada, forms: thematicTable(stems, 'lan', pada), status: d.status, generated: true }
    }

    if (lakara === 'lut') {
        if (!d.lut || !padasOf(d).includes(pada)) return null
        return { dhatuId, lakara, pada, forms: lutTable(d.lut, pada), status: d.status, generated: true }
    }

    return null
}

/** All lakaras that exist for a root and pada, in teaching order. */
export const availableLakaras = (dhatuId: string, pada: Pada): Lakara[] => {
    return LAKARA_ORDER.filter(l => getParadigm(dhatuId, l, pada) !== null)
}

export const formOf = (dhatuId: string, lakara: Lakara, pada: Pada, p: Purusha, v: Vacana): Cell | null => {
    return getParadigm(dhatuId, lakara, pada)?.forms[p][v] ?? null
}

export interface FormationStep {
    label: Bi
    expr: string
    result: string
    note?: Bi
    rule?: JoinRule | null
    /** Code-unit length of the part of `result` that came from the previous step (for highlighting). */
    keepLength?: number
}

/** Rule-aware construction steps for one cell. Irregular (stored) forms get an honest explanation instead. */
export const buildFormation = (dhatuId: string, lakara: Lakara, pada: Pada, p: Purusha, v: Vacana): FormationStep[] => {
    const d = getDhatu(dhatuId)
    const paradigm = getParadigm(dhatuId, lakara, pada)
    if (!d || !paradigm) return []

    const result = paradigm.forms[p][v][0]
    const who: Bi = {
        hi: `${PURUSHA_LABEL[p].sa.replace('ः', '')}, ${VACANA_LABEL[v].sa.replace('म्', '')}`,
        en: `${PURUSHA_LABEL[p].short} puruṣa, ${VACANA_LABEL[v].en.toLowerCase()}`
    }

    const steps: FormationStep[] = [
        {
            label: { hi: 'धातु', en: 'Root (धातु)' },
            expr: d.root,
            result: d.root,
            note: d.meaning
        }
    ]

    if (paradigm.viaDhatuId) {
        const target = getDhatu(paradigm.viaDhatuId)
        steps.push({
            label: { hi: 'धातु-आदेश', en: 'Root replacement' },
            expr: `${d.root} → ${target?.root ?? ''}`,
            result: target?.root ?? '',
            note: paradigm.note
        })

        return [...steps, ...buildFormation(paradigm.viaDhatuId, lakara, pada, p, v).slice(1)]
    }

    const source = ENDING_SOURCE[lakara]

    if (!paradigm.generated || !source) {
        steps.push({
            label: { hi: 'स्मरणीय रूप', en: 'Stored form' },
            expr: result,
            result,
            note: lakara === 'lut'
                ? {
                    hi: 'लुट्: कर्तृ-वाचक रूप (जैसे पठिता) + अस् धातु के रूप।',
                    en: 'लुट्: an agent form (like पठिता) followed by forms of अस्.'
                }
                : {
                    hi: 'यह रूप नियमित अ-अङ्ग वाले नमूने से नहीं बनता, इसलिए इसे संग्रहीत, सत्यापित रूप के रूप में सीखें। नीचे धातु की टिप्पणियाँ देखें।',
                    en: 'This form does not follow the regular अ-stem pattern, so it is taught as a stored, verified form. See the root notes below.'
                }
        })

        if (d.presentDerivation && (lakara === 'lat' || lakara === 'lan' || lakara === 'lot' || lakara === 'vidhilin')) {
            steps.push({ label: { hi: 'अङ्ग', en: 'Stem' }, expr: d.presentDerivation.expr, result, note: d.presentDerivation.note })
        }

        return steps
    }

    const isFuture = lakara === 'lrt' || lakara === 'lrn'
    let stem: string

    if (isFuture) {
        stem = d.future?.[0] ?? ''
        steps.push({
            label: { hi: 'भविष्यत् अङ्ग (स्य)', en: 'Future stem (स्य)' },
            expr: d.futureDerivation?.expr ?? `${d.root} + स्य`,
            result: stem,
            note: d.futureDerivation?.note
        })
    }
    else {
        stem = d.present?.[pada]?.[0] ?? ''
        steps.push({
            label: { hi: 'अङ्ग (विकरण सहित)', en: 'Stem (with class marker)' },
            expr: d.presentDerivation?.expr ?? `${d.root} + अ`,
            result: stem,
            note: d.presentDerivation?.note
        })
    }

    const needsAugment = lakara === 'lan' || lakara === 'lrn'

    if (needsAugment) {
        const augmented = lakara === 'lan' ? imperfectStems(d, pada)?.[0] : conditionalStems(d)?.[0]

        if (augmented) {
            const base = d.baseRootId ? getDhatu(d.baseRootId) : undefined
            const bare = d.upasarga
                ? (lakara === 'lan' ? base?.present?.P?.[0] : base?.future?.[0]) ?? stem.slice(d.upasarga.length)
                : stem

            steps.push({
                label: { hi: 'अट् आगम (अ)', en: 'Past augment अ' },
                expr: d.upasarga ? `${d.upasarga} + अ + ${bare}` : `अ + ${stem}`,
                result: augmented,
                note: d.upasarga
                    ? {
                        hi: 'अ (अट्) उपसर्ग के बाद और धातु के पहले आता है, और उपसर्ग से सन्धि करता है।',
                        en: 'The augment अ goes after the prefix and before the verb, making sandhi with the prefix.'
                    }
                    : {
                        hi: 'लङ् और लृङ् में धातु से पहले अ जुड़ता है (लुङ्लङ्लृङ्क्ष्वडुदात्तः 6.4.71)।',
                        en: 'लङ् and लृङ् add अ before the stem (लुङ्लङ्लृङ्क्ष्वडुदात्तः 6.4.71).'
                    }
            })
            stem = augmented
        }
    }

    const ending = ENDINGS[source][pada][p][v]
    const rule = JOIN_RULES[ending.join]
    const built = attach(stem, ending)

    steps.push({
        label: {
            hi: `प्रत्यय (${who.hi})`,
            en: `Ending (${who.en})`
        },
        expr: ending.join === 'zero' ? `${stem} + (${ending.show})` : `${stem} + ${ending.show}`,
        result: built,
        rule,
        keepLength: stem.length
    })

    return steps
}

/** Plain-language summary of a form's grammar, for panels and feedback. */
export const describeVerbForm = (dhatuId: string, lakara: Lakara, pada: Pada, p: Purusha, v: Vacana): Bi => {
    const d = getDhatu(dhatuId)
    const root = d?.root ?? ''
    const lak = LAKARA_LABEL[lakara]

    return {
        hi: `${root} धातु, ${lak.sa} लकार (${lak.hi}), ${PURUSHA_LABEL[p].short} पुरुष, ${VACANA_LABEL[v].hi}`,
        en: `root ${root}, ${lak.sa} (${lak.en}), ${PURUSHA_LABEL[p].short} puruṣa (${PURUSHA_LABEL[p].en.toLowerCase()}), ${VACANA_LABEL[v].en.toLowerCase()}`
    }
}

/** Every cell of every available paradigm, for indexing. */
export const allVerbCells = (dhatuId: string): Array<{ lakara: Lakara; pada: Pada; p: Purusha; v: Vacana; forms: Cell; status: ReviewStatus }> => {
    const d = getDhatu(dhatuId)
    if (!d) return []

    const out: Array<{ lakara: Lakara; pada: Pada; p: Purusha; v: Vacana; forms: Cell; status: ReviewStatus }> = []

    for (const pada of padasOf(d)) {
        for (const lakara of LAKARA_ORDER) {
            const paradigm = getParadigm(dhatuId, lakara, pada)
            if (!paradigm) continue

            for (const p of PURUSHAS) {
                for (const v of VACANAS) {
                    out.push({ lakara, pada, p, v, forms: paradigm.forms[p][v], status: paradigm.status })
                }
            }
        }
    }

    return out
}
